import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import ScrollToTop from "@/components/feature/ScrollToTop";
import WorkCard from "@/components/feature/WorkCard";
import Seo from "@/components/feature/Seo";
import { characters } from "@/mocks/characters";
import { works } from "@/mocks/works";
import { faqs, faqStructuredData } from "@/mocks/faqs";

const heroBg =
  "https://readdy.ai/api/search-image?query=stylized_anime_illustration_of_a_japanese_shinobi_village_at_warm_sunset_with_large_carved_cliff_mountain_and_waterfalls_rolling_orange_and_amber_clouds_ninja_silhouettes_on_rooftops_glowing_warm_golden_light_epic_atmospheric_flat_color_anime_background_art_high_quality&width=1600&height=900&seq=naruto-hero-2026&orientation=landscape";

const aboutBg =
  "https://readdy.ai/api/search-image?query=anime_illustration_of_a_warm_cozy_library_study_room_with_stacks_of_manga_books_and_ramen_bowl_on_a_desk_sunlit_window_golden_hour_warm_cream_and_orange_tones_cozy_reading_atmosphere_clean_flat_color_anime_style_high_quality&width=1200&height=760&seq=naruto-about-band&orientation=landscape";

const newsletterAddr = "https://readdy.ai/api/form/da1eiq0mlpfrg29fpgq0";

const faqPreview = faqs.slice(0, 4);

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg bg-background-100">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left md:px-5"
        aria-expanded={open}
      >
        <span className="text-sm font-bold text-foreground-950 md:text-base">{q}</span>
        <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-500/10 text-primary-700 transition-transform ${open ? "rotate-45" : ""}`}>
          <i className="ri-add-line" aria-hidden="true"></i>
        </span>
      </button>
      {open && (
        <p className="px-4 pb-4 text-sm leading-relaxed text-foreground-700 md:px-5 md:text-base">{a}</p>
      )}
    </div>
  );
}

export default function Home() {
  const featured = works.filter((w) => w.featured).slice(0, 6);
  const newest = [...works]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, 4);

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // Honeypot check
    const honeypot = data.get("website_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setStatus("ok");
      setMessage("订阅成功，感谢你的关注！");
      form.reset();
      return;
    }

    data.delete("website_alt");

    try {
      const res = await fetch(newsletterAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* ignore parse errors */
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam/i.test(responseText + (serverMsg || ""));
      const success = res.ok && parsed?.code === "OK" && !isSpam;

      if (success) {
        setStatus("ok");
        setMessage("订阅成功！新作品上线后我们会第一时间通知你。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          serverMsg && typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "订阅失败，请稍后再试或检查邮箱格式。",
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，订阅未成功，请稍后重试。");
    }
  };

  return (
    <>
      <ScrollToTop />
      <Seo
        title="火影同人志 - 火影忍者同人角色分类与优质同人推荐"
        description="火影同人志专注火影忍者同人作品的角色分类与推荐，收录日向雏田同人、春野樱同人、纲手同人、宇智波佐助同人、漩涡鸣人同人等热门角色作品，持续更新，陪你重温忍界的羁绊与热血。"
        keywords="火影忍者同人,日向雏田同人,春野樱同人,纲手同人,宇智波佐助同人,漩涡鸣人同人,火影同人角色"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "火影同人志",
            description: "火影忍者同人作品角色分类与推荐平台",
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "火影同人志首页",
            inLanguage: "zh-CN",
            about: "火影忍者同人作品",
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqStructuredData,
          },
        ]}
      />

      <Navbar />

      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroBg}
            alt="火影忍者同人 - 木叶忍村落日背景图"
            title="火影忍者同人角色分类网站"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/50 to-background-50"></div>
        </div>

        <div className="relative mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-24 text-center md:px-6 md:py-36">
          <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-sm text-background-50 backdrop-blur">
            <i className="ri-fire-fill text-primary-300" aria-hidden="true"></i>
            火影同人精选 · 每周更新
          </span>
          <h1 className="mt-6 max-w-3xl font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
            走进忍界的<span className="text-primary-300">同人世界</span>
            <br className="hidden md:block" /> 遇见你最爱的角色
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base">
            日向雏田、春野樱、纲手、宇智波佐助、漩涡鸣人……所有你钟爱的角色同人一网打尽。
            按角色分类浏览，发现优质二次创作，重温那份永不言弃的忍者之魂。
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/works"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-book-open-line" aria-hidden="true"></i>
              开始浏览同人
            </Link>
            <Link
              to="/characters"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-50/15 px-6 py-3 text-sm font-bold text-background-50 backdrop-blur transition-colors hover:bg-background-50/25"
            >
              <i className="ri-user-heart-line" aria-hidden="true"></i>
              查看人气角色
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
            {characters.slice(0, 5).map((c) => (
              <Link
                key={c.id}
                to={`/characters?c=${c.id}`}
                className="whitespace-nowrap rounded-full bg-background-50/15 px-4 py-1.5 text-xs font-medium text-background-100 backdrop-blur transition-colors hover:bg-primary-500/60 md:text-sm"
              >
                #{c.name}同人
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-2 gap-3 rounded-lg bg-background-100 px-4 py-6 md:grid-cols-4 md:gap-6 md:px-8 md:py-8">
          {[
            { num: "328+", label: "雏田同人作品", icon: "ri-user-heart-line" },
            { num: "296+", label: "樱同人作品", icon: "ri-flower-line" },
            { num: "1500+", label: "全网收录作品", icon: "ri-book-open-line" },
            { num: "每周", label: "持续更新", icon: "ri-refresh-line" },
          ].map((s) => (
            <div key={s.label} className="flex flex-col items-center gap-1 text-center">
              <i className={`${s.icon} text-2xl text-accent-500`} aria-hidden="true"></i>
              <span className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">{s.num}</span>
              <span className="text-xs text-foreground-600 md:text-sm">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Character categories */}
      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex items-end justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-accent-500">角色分类</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              按角色发现你的最爱
            </h2>
          </div>
          <Link
            to="/characters"
            className="hidden items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800 sm:inline-flex"
          >
            全部角色 <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5 md:gap-4">
          {characters.map((c) => (
            <Link
              key={c.id}
              to={`/characters?c=${c.id}`}
              className="group relative overflow-hidden rounded-lg bg-background-100"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={c.image}
                  alt={`${c.name} - ${c.nameJp} 火影忍者同人角色`}
                  title={`${c.name}同人作品专区`}
                  loading="lazy"
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <p className="text-[11px] text-background-200/90">{c.nameJp}</p>
                  <h3 className="font-heading text-base font-bold text-background-50 md:text-lg">{c.name}</h3>
                  <p className="mt-1 text-xs text-background-100/80">{c.count} 部同人</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured works */}
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="flex items-end justify-between">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-accent-500">编辑精选</span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                人气同人作品推荐
              </h2>
            </div>
            <Link
              to="/works"
              className="hidden items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800 sm:inline-flex"
            >
              查看全部 <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
            {featured.map((w) => (
              <WorkCard key={w.id} work={w} />
            ))}
          </div>
        </div>
      </section>

      {/* Latest updates */}
      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex items-end justify-between">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-accent-500">实时更新</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              最新上线的同人作品
            </h2>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
          {newest.map((w) => (
            <WorkCard key={w.id} work={w} />
          ))}
        </div>
      </section>

      {/* About band */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={aboutBg}
            alt="火影同人志 - 温馨的同人阅读空间"
            title="火影忍者同人作品分享平台"
            loading="lazy"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/80 via-background-950/60 to-background-950/40"></div>
        </div>

        <div className="relative mx-auto flex w-full max-w-7xl flex-col items-start px-4 py-20 md:px-6 md:py-28">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-4 py-1.5 text-xs font-medium text-background-50">
            <i className="ri-heart-3-fill" aria-hidden="true"></i>
            关于我们
          </span>
          <h2 className="mt-5 max-w-2xl font-heading text-2xl font-black text-background-50 md:text-4xl">
            为每一位忍者爱好者，打造一座同人"木叶"
          </h2>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
            我们相信，每一段未被讲述的故事、每一个角色的另一种可能，都值得被看见。
            火影同人志以角色为线索，串起无数同人创作者的心血，让雏田的告白、樱的觉醒、佐助的归途……都能被更多人读到。
          </p>
          <Link
            to="/about"
            className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            了解更多 <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-12">
          <div className="lg:col-span-2">
            <span className="text-xs font-bold uppercase tracking-widest text-accent-500">常见问题</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              你想知道的都在这里
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              关于同人作品分类、阅读方式、投稿流程与版权问题，这里为你一一解答。若仍有疑问，欢迎前往完整版常见问题页面。
            </p>
            <Link
              to="/faq"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-secondary-600"
            >
              查看全部 FAQ <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>

          <div className="space-y-3 lg:col-span-3">
            {faqPreview.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="mx-auto w-full max-w-7xl px-4 pb-16 md:px-6 md:pb-24">
        <div className="rounded-lg bg-secondary-700 px-6 py-10 md:px-12 md:py-14">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-background-50/10 px-3 py-1 text-xs font-medium text-background-50">
                <i className="ri-mail-send-line text-primary-300" aria-hidden="true"></i>
                更新订阅
              </span>
              <h2 className="mt-4 font-heading text-2xl font-black text-background-50 md:text-3xl">
                订阅更新，不错过任何新作品
              </h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-secondary-100 md:text-base">
                留下你的邮箱，每当新的火影同人作品上线，我们都会第一时间为你推送，第一时间发现你钟爱角色的新故事。
              </p>
            </div>

            <form
              onSubmit={handleSubscribe}
              data-readdy-form="同人更新订阅"
              className="flex flex-col gap-3 sm:flex-row"
            >
              <div className="flex-1">
                <label htmlFor="sub-email" className="sr-only">
                  你的邮箱
                </label>
                <input
                  id="sub-email"
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="请输入你的邮箱"
                  className="w-full rounded-md bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                立即订阅 <i className="ri-send-plane-fill" aria-hidden="true"></i>
              </button>
              {/* Honeypot (anti-spam) */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />
            </form>
            {status !== "idle" && (
              <p
                className={`mt-3 text-sm ${
                  status === "ok" ? "text-primary-300" : "text-accent-300"
                } lg:col-span-2 lg:mt-0`}
              >
                {message}
              </p>
            )}
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </>
  );
}